/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { errorMessage } from '@/util/errorUtil'
import {
    compareVersions,
    createPersistentItem,
    isSilentError,
    logDebug,
    logError,
    logInfo,
    logWarn,
    parseVersion,
    requestWithRetry,
    sleep,
    type Version,
} from '@amazon/vinyl-util'
import { string } from '@amazon/vinyl-validation'

import { ctx } from '@/util/context'
import { bullfrogVersion } from '@/version'
import { toast, toastError } from '@/components/toast'
import { getStringAsync } from '@/ota/i18n/bundle.generated'
import { IS_TAURI } from '@/util/platform'

const { target } = ctx('ota')

/** The live site, kept current by the deploy job (serves version.txt + releases/<version>.zip). */
const UPDATE_BASE_URL = 'https://amp.music.amazon.dev'
// Don't compete with first-load bandwidth
const STARTUP_DELAY_SECONDS = 20
// Keep the active version plus one previous (in case we ever want a rollback).
const KEEP_BUNDLES = 2
// The staged-update toast is the only notice of a pending update and arrives well after launch, so
// it gets longer than the 3s default to actually be noticed.
const READY_TOAST_SECONDS = 20

/**
 * The last version we've shown the user, so the next launch can tell "this bundle is new to you"
 * from "already seen". Absent on a fresh install — see {@link announceAppliedUpdate}.
 */
const lastSeenVersion = createPersistentItem<string | null>({
    key: 'ota.lastSeenVersion',
    defaultValue: null,
    validator: string().orNull().assert,
})

/**
 * Schedules a background OTA check on Tauri. No-op on web — the web origin
 * already serves the latest assets on every reload.
 *
 * Update rules:
 * - Only updates within the same major version (1.0.1 → 1.5.x ✅, 1.0.1 → 2.0.0 ✗).
 * - Compares versions with Vinyl's `compareVersions` semantics.
 * - One round-trip: downloads `releases/<version>.zip`, hands the bytes to
 *   Rust which extracts into a sibling `<version>.partial` dir then renames
 *   to the final dir name. Activation is a single tmp-then-rename of the
 *   `bundles/active` pointer.
 * - Toasts when a new bundle is ready. The new version applies on next launch.
 */
export function installOtaUpdater(): void {
    logDebug(target, 'installOtaUpdater')
    if (!IS_TAURI) return
    ;(async () => {
        try {
            // Confirm a previously-staged update right away — the bundle we're running IS the
            // update, so this belongs at launch rather than behind the network-check delay.
            await announceAppliedUpdate()
            await sleep(STARTUP_DELAY_SECONDS)
            await checkAndApplyUpdate()
        } catch (e) {
            if (isSilentError(e)) return
            logError(target, e)
            const detail = errorMessage(e)
            toastError(getStringAsync('dmusic_ota_update_failed', { detail }))
        }
    })().catch((e: unknown) => logError(target, e))
}

async function checkAndApplyUpdate(): Promise<void> {
    const remoteVersionStr = await stage('check version', async () => {
        const res = await requestWithRetry(`${UPDATE_BASE_URL}/version.txt`)
        return (await res.text()).trim()
    })
    // `version.txt` is server-controlled, but a bad deploy or a proxy's error
    // page could still hand back text with no parseable version — treat that
    // as "no update" rather than crash on `remote.major` below.
    const remote = parseVersion(remoteVersionStr) as Version | null
    if (!remote) {
        logWarn(target, 'unparseable remote version, skipping', { remoteVersionStr })
        return
    }
    if (!isCompatibleUpdate(bullfrogVersion, remote)) {
        logDebug(target, 'no update', { current: bullfrogVersion.str, remote: remote.str })
        return
    }

    // Dynamic import keeps the Tauri runtime in its own lazily-loaded chunk
    // — only fetched once we've actually decided to talk to Rust.
    const { invoke } = await import('@tauri-apps/api/core')

    // If we've already activated this version on a previous run, skip.
    const active = await stage('check active version', () =>
        invoke<string | null>('ota_active_version')
    )
    if (active === remote.str) {
        logDebug(target, 'remote already activated', remote.str)
        return
    }

    logInfo(target, 'downloading update', { current: bullfrogVersion.str, remote: remote.str })

    const zipBytes = await stage(`download ${remote.str}.zip`, async () => {
        const zipUrl = `${UPDATE_BASE_URL}/releases/${remote.str}.zip`
        return (await requestWithRetry(zipUrl)).arrayBuffer()
    })

    // Send raw bytes via Tauri's binary IPC — the JS-side base64 path used
    // String.fromCharCode(...bytes) which blows the call stack on multi-MB
    // bundles. The args slot is reserved for the body, so the version goes
    // through a custom request header instead.
    await stage('install', () =>
        invoke('ota_install_zip', new Uint8Array(zipBytes), {
            headers: { 'x-bullfrog-version': remote.str },
        })
    )
    await stage('activate', () => invoke('ota_activate', { version: remote.str }))

    invoke('ota_prune', { keep: KEEP_BUNDLES }).catch((e) => logWarn(target, 'prune failed', e))

    logInfo(target, 'update ready', remote.str)
    toast(getStringAsync('dmusic_ota_update_ready'), { time: READY_TOAST_SECONDS })
}

/**
 * On the first launch after an update was activated, confirm the new version.
 *
 * The running JS comes from the active bundle, so {@link bullfrogVersion} already IS the applied
 * version — we only need to know whether the user has seen it. A persisted marker supplies that:
 * when it's absent (fresh install) we record the version silently, because announcing an "update"
 * for a first run would be a lie.
 */
async function announceAppliedUpdate(): Promise<void> {
    const current = bullfrogVersion.str
    const seen = await lastSeenVersion.get()
    if (seen === current) return

    if (seen != null && compareVersions(bullfrogVersion, parseVersion(seen)) > 0) {
        logInfo(target, 'update applied', { from: seen, to: current })
        toast(getStringAsync('dmusic_ota_updated', { version: current }))
    }
    await lastSeenVersion.set(current)
}

/**
 * Wraps a step so any thrown error includes the step name. The outer catch
 * surfaces this as `Update failed: <step>: <reason>` in the error toast.
 * A silent error (e.g. an aborted request) is rethrown as-is — naming the
 * stage would be wasted on a failure the outer catch is about to swallow.
 */
async function stage<T>(name: string, fn: () => Promise<T>): Promise<T> {
    logDebug(target, `stage ${name} start`)
    try {
        return await fn()
    } catch (e) {
        if (isSilentError(e)) throw e
        const detail = errorMessage(e)
        throw new Error(`${name}: ${detail}`)
    }
}

function isCompatibleUpdate(current: Version, remote: Version): boolean {
    if (remote.major !== current.major) return false
    return compareVersions(remote, current) > 0
}
